import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHero, type PageHeroTone } from '@/components/ui/PageHero'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/lib/cn'

export type TradingViewWidgetKind = 'economic_calendar' | 'timeline'

const WIDGET_PATH: Record<TradingViewWidgetKind, string> = {
  economic_calendar: 'events',
  timeline: 'timeline',
}

/**
 * Full-bleed TradingView iframe widget (economic calendar / news timeline).
 */
export function TradingViewEmbed({
  kind,
  className,
  title,
  eyebrow,
  subtitle,
  tone = 'info',
}: {
  kind: TradingViewWidgetKind
  className?: string
  title?: string
  eyebrow?: string
  subtitle?: string
  tone?: PageHeroTone
}) {
  const { i18n } = useTranslation()
  const { isDark } = useTheme()

  const src = useMemo(() => {
    const locale = (i18n.language || 'en').split('-')[0]
    const params = new URLSearchParams({
      locale,
      colorTheme: isDark ? 'dark' : 'light',
      isTransparent: 'true',
      width: '100%',
      height: '100%',
    })

    if (kind === 'economic_calendar') {
      params.set('importanceFilter', '-1,0,1')
    } else {
      params.set('feedMode', 'all_symbols')
      params.set('displayMode', 'regular')
    }

    return `https://s.tradingview.com/embed-widget/${WIDGET_PATH[kind]}/?${params.toString()}`
  }, [kind, i18n.language, isDark])

  return (
    <div className={cn('flex h-full min-h-0 flex-col bg-terminal-bg', className)}>
      {title ? (
        <PageHero
          tone={tone}
          glow={kind === 'economic_calendar' ? 'start' : 'end'}
          eyebrow={eyebrow ?? title}
          title={title}
          subtitle={subtitle}
        />
      ) : null}
      <iframe
        key={src}
        title={title ?? kind}
        src={src}
        className="min-h-0 w-full flex-1 border-0 bg-terminal-bg"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allow="clipboard-write"
      />
    </div>
  )
}
